import { useState, useEffect } from "react";
import { Check, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { allPlatformsData } from "@/data/content";

// Platform to blog article mapping
const platformToBlog = {
  // Quick Commerce
  "Myntra Now": "myntra-onboarding-guide-2025",
  "Blinkit": "blinkit-vendor-registration",
  "Big Basket": "big-basket-seller-registration",
  "BigBasket": "big-basket-seller-registration",
  "Swiggy Instamart": "swiggy-instamart-onboarding",
  "Flipkart Minutes": "flipkart-minutes-launch-strategy",
  "Amazon Fresh": "amazon-seller-central-india",
  "JioMart Express": "quick-commerce-platforms-comparison",
  "Pincode": "quick-commerce-platforms-comparison",
  "Slikk Club": "quick-commerce-platforms-comparison",
  
  // Premium Platforms
  "Myntra": "myntra-onboarding-guide-2025",
  "Nykaa": "nykaa-onboarding-complete-process",
  "Nykaa Fashion": "nykaa-fashion-seller-guide",
  "Ajio": "myntra-onboarding-guide-2025",
  "Tata Cliq": "tata-cliq-luxury-onboarding",
  "Tata Cliq Luxury": "tata-cliq-luxury-onboarding",
  "Amazon": "amazon-seller-central-india",
  "Flipkart": "flipkart-seller-onboarding",
  "Meesho": "meesho-supplier-registration",
  "Purplle": "nykaa-onboarding-complete-process",
  
  // Luxe Platforms
  "Myntra Luxe": "myntra-private-label-strategy",
  "Aza Fashions": "tata-cliq-luxury-onboarding",
  "Pernia": "tata-cliq-luxury-onboarding",
  "Tata Cliq Palette": "tata-cliq-luxury-onboarding",
  
  // International
  "Amazon US": "amazon-us-international-seller",
  "Amazon UAE": "noon-uae-seller-registration",
  "Walmart": "walmart-marketplace-india-brands",
  "Noon": "noon-uae-seller-registration",
  "Noon UAE": "noon-uae-seller-registration",
  
  // Private Labeling
  "Myntra Private Label": "myntra-private-label-strategy",
  "Nykaa Private Label": "nykaa-cosmetics-private-labeling",
  "Amazon Private Label": "amazon-seller-central-india",
  
  // Starter
  "Snapdeal": "flipkart-seller-onboarding",
  "ShopClues": "flipkart-seller-onboarding",
  "Paytm Mall": "flipkart-seller-onboarding",
  "JioMart": "quick-commerce-platforms-comparison",
  "Limeroad": "myntra-onboarding-guide-2025",
};

const ServicesWithTabs = () => {
  const [activeTab, setActiveTab] = useState("starter");
  const navigate = useNavigate();

  // Listen for tab change from navbar dropdown
  useEffect(() => {
    const handleTabChange = (e) => setActiveTab(e.detail);
    window.addEventListener('setServiceTab', handleTabChange);
    return () => window.removeEventListener('setServiceTab', handleTabChange);
  }, []);

  const tabCategories = [
    { id: "starter", label: "Starter Platforms", color: "emerald", data: allPlatformsData.starter },
    { id: "premium", label: "Premium Platforms", color: "blue", data: allPlatformsData.premium },
    { id: "luxe", label: "Luxe Platforms", color: "purple", data: allPlatformsData.luxe },
    { id: "international", label: "International", color: "slate", data: allPlatformsData.international },
    { id: "quickCommerce", label: "Quick Commerce", color: "red", data: allPlatformsData.quickCommerce },
    { id: "privateLabeling", label: "Private Labeling", color: "amber", data: allPlatformsData.privateLabeling }
  ];

  const getColorConfig = (color, active = false) => {
    const colors = {
      emerald: {
        gradient: "from-emerald-500 to-teal-600",
        bg: active ? "bg-emerald-500" : "bg-emerald-50",
        text: active ? "text-white" : "text-emerald-700",
        cardGradient: "from-emerald-50 to-teal-50",
        cardBorder: "border-emerald-200 hover:border-emerald-400",
        iconBg: "bg-emerald-100",
        iconText: "text-emerald-600",
        glow: "hover:shadow-emerald-200"
      },
      blue: {
        gradient: "from-blue-500 to-indigo-600",
        bg: active ? "bg-blue-500" : "bg-blue-50",
        text: active ? "text-white" : "text-blue-700",
        cardGradient: "from-blue-50 to-indigo-50",
        cardBorder: "border-blue-200 hover:border-blue-400",
        iconBg: "bg-blue-100",
        iconText: "text-blue-600",
        glow: "hover:shadow-blue-200"
      },
      purple: {
        gradient: "from-purple-500 to-violet-600",
        bg: active ? "bg-purple-500" : "bg-purple-50",
        text: active ? "text-white" : "text-purple-700",
        cardGradient: "from-purple-50 to-violet-50",
        cardBorder: "border-purple-200 hover:border-purple-400",
        iconBg: "bg-purple-100",
        iconText: "text-purple-600",
        glow: "hover:shadow-purple-200"
      },
      slate: {
        gradient: "from-slate-600 to-slate-800",
        bg: active ? "bg-slate-600" : "bg-slate-50",
        text: active ? "text-white" : "text-slate-700",
        cardGradient: "from-slate-50 to-gray-50",
        cardBorder: "border-slate-200 hover:border-slate-400",
        iconBg: "bg-slate-100",
        iconText: "text-slate-600",
        glow: "hover:shadow-slate-200"
      },
      red: {
        gradient: "from-red-500 to-rose-600",
        bg: active ? "bg-red-500" : "bg-red-50",
        text: active ? "text-white" : "text-red-700",
        cardGradient: "from-red-50 to-rose-50",
        cardBorder: "border-red-200 hover:border-red-400",
        iconBg: "bg-red-100",
        iconText: "text-red-600",
        glow: "hover:shadow-red-200"
      },
      amber: {
        gradient: "from-amber-500 to-orange-600",
        bg: active ? "bg-amber-500" : "bg-amber-50",
        text: active ? "text-white" : "text-amber-700",
        cardGradient: "from-amber-50 to-orange-50",
        cardBorder: "border-amber-200 hover:border-amber-400",
        iconBg: "bg-amber-100",
        iconText: "text-amber-600",
        glow: "hover:shadow-amber-200"
      }
    };
    return colors[color] || colors.blue;
  };

  const handlePlatformClick = (platform) => {
    const blogId = platformToBlog[platform] || "quick-commerce-platforms-comparison";
    navigate(`/blog/${blogId}`);
  };

  const activeCategory = tabCategories.find(cat => cat.id === activeTab);
  const colorConfig = getColorConfig(activeCategory.color);

  return (
    <section id="services" className="py-20 md:py-28 px-4 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden" data-testid="services-section">
      {/* Background 3D Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-red-100 to-orange-100 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-40" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-emerald-50 to-teal-50 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header with 3D effect */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full text-sm font-semibold mb-6 shadow-lg shadow-red-200">
            <Sparkles size={16} />
            70+ PLATFORMS
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Our Platform{" "}
            <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
              Onboarding Services
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Choose from 6 specialized categories covering India's top marketplaces and global platforms
          </p>
        </div>

        {/* 3D Tab Pills */}
        <div className="hidden lg:flex justify-center gap-3 mb-16 flex-wrap perspective-1000">
          {tabCategories.map((tab) => {
            const isActive = activeTab === tab.id;
            const tabColors = getColorConfig(tab.color, isActive);
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-6 py-3.5 rounded-2xl font-semibold transition-all duration-300
                  ${isActive 
                    ? `bg-gradient-to-r ${tabColors.gradient} text-white shadow-xl transform -translate-y-1` 
                    : `bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg`
                  }
                `}
                style={{
                  transform: isActive ? 'translateY(-4px) scale(1.02)' : 'translateY(0)',
                  boxShadow: isActive ? '0 20px 40px -10px rgba(0,0,0,0.2)' : ''
                }}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Mobile Dropdown */}
        <div className="lg:hidden mb-10">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl text-gray-900 font-semibold focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white shadow-lg"
          >
            {tabCategories.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>

        {/* Active Tab Content with 3D Card */}
        <div className="animate-fadeIn">
          <div 
            className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 relative overflow-hidden"
            style={{
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.02)'
            }}
          >
            {/* Decorative gradient corner */}
            <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${colorConfig.cardGradient} rounded-bl-full opacity-50`} />
            
            <div className="relative z-10">
              {/* Category Header */}
              <div className="text-center mb-10">
                <span className={`inline-block px-4 py-1.5 bg-gradient-to-r ${colorConfig.gradient} text-white text-sm font-bold rounded-full mb-4`}>
                  {activeCategory.data.platforms.length}+ Platforms
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  {activeCategory.data.title}
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                  Expert onboarding and account management. Click any platform to learn more.
                </p>
              </div>

              {/* Interactive Platform Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-12">
                {activeCategory.data.platforms.map((platform, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePlatformClick(platform)}
                    className={`
                      group relative bg-gradient-to-br ${colorConfig.cardGradient} 
                      border-2 ${colorConfig.cardBorder} rounded-2xl p-5
                      transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl ${colorConfig.glow}
                      cursor-pointer text-left
                    `}
                    data-testid={`platform-${idx}`}
                  >
                    {/* Hover overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${colorConfig.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
                    
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 ${colorConfig.iconBg} rounded-lg flex items-center justify-center`}>
                          <Check className={colorConfig.iconText} size={16} />
                        </div>
                        <ExternalLink className="text-gray-400 group-hover:text-red-500 transition-colors" size={16} />
                      </div>
                      <span className="font-bold text-gray-900 text-sm block group-hover:text-gray-800">
                        {platform}
                      </span>
                      <span className="text-xs text-gray-500 mt-1 block group-hover:text-red-500 transition-colors">
                        Read Guide →
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Service Features - 3D Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                  { title: "Complete Documentation", desc: "GST, brand authorization & compliance", icon: "📋" },
                  { title: "Product Cataloging", desc: "SEO-optimized professional listings", icon: "📦" },
                  { title: "Fast-Track Approval", desc: "2-4 weeks vs 6-8 weeks DIY", icon: "⚡" },
                  { title: "Account Management", desc: "Ongoing support & growth strategies", icon: "📈" }
                ].map((feature, idx) => (
                  <div 
                    key={idx}
                    className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h5 className="font-bold text-gray-900 mb-1">{feature.title}</h5>
                    <p className="text-sm text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <button
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-10 py-5 rounded-2xl font-bold text-lg inline-flex items-center gap-3 shadow-xl shadow-red-200 hover:shadow-2xl hover:shadow-red-300 transition-all duration-300 transform hover:-translate-y-1"
                >
                  Get Started with {activeCategory.data.title}
                  <ArrowRight size={22} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA - 3D Glass Card */}
        <div className="mt-20 text-center">
          <div 
            className="bg-gradient-to-r from-red-500 via-orange-500 to-red-500 rounded-3xl p-1"
            style={{
              boxShadow: '0 25px 50px -12px rgba(239, 68, 68, 0.4)'
            }}
          >
            <div className="bg-white rounded-[22px] p-10 md:p-14">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Not Sure Which Platform Fits Your Brand?
              </h3>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Our experts will analyze your brand and recommend the perfect platforms for maximum growth
              </p>
              <button
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-10 py-5 rounded-2xl font-bold text-lg inline-flex items-center gap-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                Get Free Consultation
                <ArrowRight size={22} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        .perspective-1000 {
          perspective: 1000px;
        }
      `}</style>
    </section>
  );
};

export default ServicesWithTabs;
